import { makeAutoObservable } from 'mobx';
import qs from 'qs';

import { axiosClient } from '../config/axios-client';
import {
  LobbyTableCustomSorting,
  LobbyTableCustomSortingProperty,
} from '../model/lobbytable/LobbyTableCustomSorting';
import { LobbyTablePage } from '../model/lobbytable/LobbyTableResponse';
import {
  LobbyTableSearchFilter,
  LobbyTableSearchFilterOption,
} from '../model/lobbytable/LobbyTableSearchFilter';
import { SortDirection } from '../model/SortDirection';
import { getEnumKeyByValue } from '../utils/enum.utils';
import sessionStorageStore from './SessionStorageStore';
import userSettingsStore from './UserSettingsStore';

const BASE_LOBBY_TABLES_PATH = '/lobby-tables';

const DEFAULT_SEARCH_FILTER: LobbyTableSearchFilter = {
  clubs: [],
  leagues: [],
  superLeagues: [],
  gameTypes: [],
  limits: [],
  searchName: '',
  onlyAvailable: false,
};

class LobbyTableStore {
  public searchFilter: LobbyTableSearchFilter = DEFAULT_SEARCH_FILTER;
  public sorting: LobbyTableCustomSorting = {};

  public constructor() {
    makeAutoObservable(this);
    this.searchFilter =
      sessionStorageStore.getLobbyTableSearchFilter() ?? DEFAULT_SEARCH_FILTER;
  }

  public getLobbyTablePage = (
    page: number,
    size: number,
    club?: string,
  ): Promise<LobbyTablePage> =>
    axiosClient
      .get<LobbyTablePage>(BASE_LOBBY_TABLES_PATH, {
        params: {
          ...(userSettingsStore.settings.table.expertMode && club
            ? this.getSearchFilterExpertMode(club)
            : this.searchFilter),
          ...{
            ...this.sorting,
            sortProperty: getEnumKeyByValue(
              LobbyTableCustomSortingProperty,
              this.sorting.sortProperty,
            ),
          },
          page,
          size,
        },
        paramsSerializer: params => {
          return qs.stringify(params, { arrayFormat: 'repeat' });
        },
      })
      .then(response => response.data);

  private getSearchFilterExpertMode = (
    club: string,
  ): LobbyTableSearchFilter => {
    return {
      ...DEFAULT_SEARCH_FILTER,
      clubs: [club],
      onlyAvailable: true,
      searchName: this.searchFilter.searchName,
    };
  };

  public updateFilterOptions = (
    options: LobbyTableSearchFilterOption,
  ): void => {
    this.searchFilter = { ...this.searchFilter, ...options };
    sessionStorageStore.setLobbyTableSearchFilter(this.searchFilter);
  };

  public resetFilter = (): void => {
    this.searchFilter = DEFAULT_SEARCH_FILTER;
    sessionStorageStore.setLobbyTableSearchFilter(this.searchFilter);
  };

  public setSortProperty = (
    sortProperty: LobbyTableCustomSortingProperty,
  ): void => {
    this.sorting.sortProperty = sortProperty;
  };

  public setSortDirection = (direction: SortDirection): void => {
    this.sorting.sortDirection = direction;
  };
}

const lobbyTableStore = new LobbyTableStore();
export default lobbyTableStore;
